import React, { useEffect, useState } from 'react';
import { GetJson, PutJson, PostJson } from '@awp/helpers/fetchReact';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { FORM_MODE, Permissions, REST_API, Usertypes } from '@awp/constants';
import * as Yup from 'yup';

import { selectCurrentUser } from '@awp/stores/usersSlice';
import { selectUserTypes } from '@awp/stores/dropdownsSlice';

import { Card, Button, Spinner, Container, Row, Col } from 'react-bootstrap';
import { FormInput, FormTextArea, FormPhoneNumber } from '@awp/components/Common/AwpFormInputs';
import { hasAccess } from '@components/Authorize';
import { Formik, Form } from 'formik';
import { toast } from 'react-toastify';
import ConditionTable from './ConditionTable';
import AwpYesNoModal from '@components/Common/AwpYesNoModal';
import AwpValidationErrorModal from '@awp/components/Common/AwpValidationErrorModal';
import ConditionEditModal from './ConditionEditModal';
import AwpCloseCancelButtons from '../../components/AwpCloseCancelButton';
import { getCrud } from '@awp/shared/Crud';

const ForecastRecipientEdit = ({
  setMode,
  mode,
  setForecastRecipientId,
  forecastRecipientId,
  data,
  setData,
  setLoading,
  loading,
}) => {
  const userTypesDropdown = useSelector(selectUserTypes);
  const currentUser = useSelector(selectCurrentUser);
  const [errors, setErrors] = useState(null);
  const [unregisteredUser, setUnregisteredUser] = useState(true);
  const [showConditionEditModal, setShowConditionEditModal] = useState({ show: false });
  const externalUserType = userTypesDropdown.options.find((x) => x.label === Usertypes.External);
  const defaultDto = {
    id: null,
    userType: Usertypes.External,
    firstName: '',
    lastName: '',
    organization: '',
    email: '',
    fax: '',
    mobile: '',
    note: '',
    regularCondition: {
      Id: 'regularCondition',
      Distribution: 'Regular',
      AvalancheAreaIds: [],
      AvalancheAreas: '',
    },
    transitionCondition: {
      Id: 'transitionCondition',
      Distribution: 'Transition',
      AvalancheAreaIds: [],
      AvalancheAreas: '',
    },
  };
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [dto, setDto] = useState(defaultDto);

  const crud = getCrud('Forecast Recipient', mode);
  const { readOnly, title, successMessage } = crud;

  useEffect(() => {
    if (mode === FORM_MODE.CREATE) {
      setDto(defaultDto);
    } else {
      setLoading(true);
      const url = REST_API.FORECAST_RECIPIENTS + forecastRecipientId;

      GetJson(url)
        .then((response) => response.json())
        .then((entity) => {
          setUnregisteredUser(entity.UserTypeId === externalUserType.value);
          setDto(entityToDto(entity));
          setLoading(false);
        });
    }
  }, []);

  const entityToDto = (entity) => {
    const dtoTemp = {
      id: entity.Id,
      userType: userTypesDropdown.options.find((x) => x.value === entity.UserTypeId).label,
      lastName: entity.LastName,
      firstName: entity.FirstName,
      organization: entity.Organization,
      email: entity.Email,
      fax: entity.Fax,
      mobile: entity.Mobile,
      note: entity.Note,
      regularCondition: entity.RegularCondition,
      transitionCondition: entity.TransitionCondition,
    };

    return dtoTemp;
  };

  const dtoToEntity = (values) => {
    const entity = {
      Id: values.id ?? 0,
      LastName: values.lastName,
      FirstName: values.firstName,
      Organization: values.organization,
      Email: values.email,
      Fax: values.fax,
      Mobile: values.mobile,
      Note: values.note,
      RegularCondition: values.regularCondition,
      TransitionCondition: values.transitionCondition,
    };

    if (values.isPrivate === 'Y') {
      entity.ForecastRecipientSubscriptions = values.weatherStations.map((weatherStation) => ({
        SecurityIdentifierId: currentUser.SecurityIdentifierId,
        WeatherStationId: weatherStation.value,
      }));
    }

    return entity;
  };

  const createRecipient = (values, setSubmitting, resetForm) => {
    setSubmitting(true);
    const entity = dtoToEntity(values);
    const url = REST_API.FORECAST_RECIPIENTS;
    PostJson(url, entity)
      .then(async (response) => {
        if (response.ok) {
          toast.success(successMessage);
          const entity = await response.json();
          const dto = entityToDto(entity);
          updateData(entity);
          setForecastRecipientId(entity.Id);
          setMode(FORM_MODE.EDIT);
          resetForm({
            values: {
              ...dto,
              validateOnMount: true,
            },
          });
        } else if (response.status === 412) {
          const errors = await response.json();
          setErrors(errors);
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };
  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setSubmitting(true);
    const entity = dtoToEntity(values);
    if (mode === FORM_MODE.CREATE) {
      const precheck = await PostJson(REST_API.FORECAST_RECIPIENTS + 'exists', entity);
      if (precheck.ok) {
        const precheckResult = await precheck.json();
        if (precheckResult.exists) {
          setShowSaveConfirm(true);
        } else {
          createRecipient(values, setSubmitting, resetForm);
        }
      }
    } else {
      const url = REST_API.FORECAST_RECIPIENTS + forecastRecipientId;
      entity.Id = forecastRecipientId;
      PutJson(url, entity)
        .then(async (response) => {
          if (response.ok) {
            toast.success(successMessage);
            const entity = await response.json();
            updateData(entity);
            resetForm({
              values: {
                ...values,
                validateOnMount: true,
              },
            });
          } else if (response.status === 412) {
            const errors = await response.json();
            setErrors(errors);
          }
        })
        .finally(() => {
          setSubmitting(false);
        });
    }
  };

  const updateData = (entity) => {
    if (mode === FORM_MODE.EDIT) {
      const index = data.findIndex((x) => x.Id === entity.Id);
      if (index < 0) return;
      data[index] = entity;
      setData([...data]);
    } else {
      setData([...data, entity]);
    }
  };

  const Buttons = ({ props }) => {
    return mode === FORM_MODE.EDIT || mode === FORM_MODE.CREATE ? (
      <AwpCloseCancelButtons
        props={props}
        isPristine={!props.dirty || props.isSubmitting}
        hasAccess={hasAccess([Permissions.WriteAvalancheForm])}
        onConfirmedCancel={() => {
          props.resetForm();
          setMode(FORM_MODE.LIST);
        }}
      />
    ) : (
      <>
        <div className="mr-2" style={{ float: 'left' }}>
          {mode === FORM_MODE.VIEW && (
            <Button className="mr-2" variant="outline-primary" onClick={() => setMode('edit')}>
              Edit
            </Button>
          )}
          <Button className="mr-2" variant="outline-primary" onClick={() => setMode('list')}>
            Close
          </Button>
        </div>
      </>
    );
  };

  Buttons.propTypes = {
    title: PropTypes.string,
    props: PropTypes.any,
    resetForm: PropTypes.func,
    isSubmitting: PropTypes.bool,
    isValid: PropTypes.bool,
    dirty: PropTypes.bool,
  };

  return (
    <>
      {loading ? (
        <Container>
          <Spinner animation="border" />
        </Container>
      ) : (
        <>
          <Formik
            initialValues={dto}
            validationSchema={Yup.object({
              firstName: Yup.string()
                .label('First name')
                .max(50, 'Maximum 50 characters are allowed')
                .required('First name is a required field'),
              lastName: Yup.string()
                .label('Last name')
                .max(50, 'Maximum 50 characters are allowed')
                .required('Last name is a required field'),
              organization: Yup.string()
                .when('userType', {
                  is: Usertypes.External,
                  then: Yup.string()
                    .label('Organization')
                    .max(100, 'Maximum 100 characters are allowed')
                    .required('Organization is required'),
                })
                .optional()
                .nullable(),
              email: Yup.string()
                .label('Email')
                .email()
                .max(320, 'Maximum 320 characters are allowed')
                .optional()
                .nullable()
                .test(
                  'email',
                  'The email or fax number is required. Enter a fax number when the recipient is a Fax machine',
                  (value, context) => {
                    if (context.parent.fax) return true;
                    return value;
                  },
                ),
              fax: Yup.string()
                .label('Fax number')
                .test('fax-number-length', 'Fax number must be 10 digits', (value) => {
                  if (value) {
                    return value && (value.length === 10 || value.length === 0);
                  } else {
                    return true;
                  }
                })
                .optional()
                .nullable()
                .test(
                  'fax',
                  'The email or fax number is required. Enter a fax number when the recipient is a Fax machine',
                  (value, context) => {
                    if (context.parent.email) return true;
                    return value;
                  },
                ),
              mobile: Yup.string()
                .label('Business cellular number')
                .test('phone-number-length', 'Business cellular number must be 10 digits', (value) => {
                  if (value) {
                    return value && (value.length === 10 || value.length === 0);
                  } else {
                    return true;
                  }
                })
                .optional()
                .nullable(),
              note: Yup.string().label('Note').max(2000, 'Maximum 2000 characters are allowed').optional().nullable(),
            })}
            onSubmit={handleSubmit}
          >
            {(props) => (
              <>
                {/* {props.errors && <pre>{JSON.stringify(props.errors, null, 2)}</pre>} */}
                {/* {props.values && <pre>{JSON.stringify(props.values, null, 2)}</pre>} */}
                {/* {props.touched && <pre>{JSON.stringify(props.touched, null, 2)}</pre>} */}
                <Form>
                  <Card
                    border="light"
                    className="page-card"
                    style={{ border: '0px', boxShadow: '0px 0px 0px #000000' }}
                  >
                    <Card.Header style={{ backgroundColor: 'white' }}>
                      <Row>
                        <Col sm={8}>{crud.getHeader()}</Col>
                      </Row>
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col sm={6} className="mb-4">
                          <FormInput label="User Type" readOnly={true} name="userType" id="userType" />
                        </Col>
                      </Row>
                      <Row>
                        <Col sm={6} className="mb-4">
                          <FormInput
                            label="Last Name"
                            readOnly={readOnly || !unregisteredUser}
                            name="lastName"
                            id="lastName"
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col sm={6} className="mb-4">
                          <FormInput
                            label="First Name"
                            readOnly={readOnly || !unregisteredUser}
                            name="firstName"
                            id="firstName"
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col sm={6} className="mb-4">
                          <FormInput
                            label="Organization"
                            readOnly={readOnly || !unregisteredUser}
                            name="organization"
                            id="organization"
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col sm={6} className="mb-4">
                          <FormInput label="Email" readOnly={readOnly || !unregisteredUser} name="email" id="email" />
                        </Col>
                      </Row>
                      <Row>
                        <Col sm={6} className="mb-4">
                          <FormPhoneNumber
                            label="Fax Number"
                            readOnly={readOnly}
                            name="fax"
                            id="fax"
                            setFieldValue={props.setFieldValue}
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col sm={6} className="mb-4">
                          <FormPhoneNumber
                            label="Business Cellular Number"
                            readOnly={readOnly}
                            name="mobile"
                            id="mobile"
                            setFieldValue={props.setFieldValue}
                          />
                        </Col>
                      </Row>
                      <Row>
                        <Col sm={8} className="mb-4">
                          <FormTextArea readOnly={readOnly} label="Note" id="note" name="note" rows={3} />
                        </Col>
                      </Row>
                      <ConditionTable
                        conditions={[props.values.regularCondition, props.values.transitionCondition]}
                        setShowConditionEditModal={setShowConditionEditModal}
                        loading={loading}
                        readOnly={readOnly}
                        setFieldValue={props.setFieldValue}
                      />
                      <Buttons title=" " props={props} />
                      {showConditionEditModal?.show && (
                        <ConditionEditModal
                          showConditionEditModal={showConditionEditModal}
                          setShowConditionEditModal={setShowConditionEditModal}
                          setFieldValue={props.setFieldValue}
                          setTouched={props.setTouched}
                        />
                      )}
                    </Card.Body>
                  </Card>
                </Form>
                <AwpYesNoModal
                  showYesNoModal={showSaveConfirm}
                  setShowYesNoModal={setShowSaveConfirm}
                  onClickYes={() => {
                    setShowSaveConfirm(false);
                    createRecipient(props.values, props.setSubmitting, props.resetForm);
                  }}
                  title="Duplicate record"
                  body="The historical records already exist for this recipient, and saving will link all the historical data to this Recipient. Do you still want to save?"
                />
              </>
            )}
          </Formik>
          <AwpValidationErrorModal setErrors={setErrors} errors={errors} />
        </>
      )}
    </>
  );
};

ForecastRecipientEdit.propTypes = {
  setMode: PropTypes.func,
  mode: PropTypes.string,
  setForecastRecipientId: PropTypes.func,
  forecastRecipientId: PropTypes.any,
  data: PropTypes.array,
  setData: PropTypes.func,
  setLoading: PropTypes.func,
  loading: PropTypes.bool,
};

export default ForecastRecipientEdit;
